import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Field, PasswordInput } from "@/components/auth/Field";
import { PasswordPolicyHint, usePasswordPolicy } from "@/components/auth/PasswordPolicyHint";
import { Spinner } from "@/components/common/FullScreenLoader";
import { api, apiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const policy = usePasswordPolicy();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(event) {
    event.preventDefault();
    if (mismatch) return;
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/password/reset", { token, new_password: password });
      setDone(true);
    } catch (e) {
      const err = apiError(e);
      setError(err.errors?.length ? err.errors.join(" ") : err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout eyebrow="Account recovery" title="Link incomplete." subtitle="This reset link is missing its token.">
        <Link to="/forgot-password" data-testid="reset-password-request-new-link" className="bz-btn-secondary">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout eyebrow="Account recovery" title="Password updated." subtitle="All previous sessions were signed out for safety.">
        <div data-testid="reset-password-success" className="space-y-5">
          <div className="flex items-center gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
            <ShieldCheck className="h-5 w-5 flex-none text-emerald-400" strokeWidth={1.75} />
            Your new passphrase is active.
          </div>
          <Link to="/sign-in" data-testid="reset-password-signin-link" className="bz-btn-primary w-full">
            Continue to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout eyebrow="Account recovery" title="Choose a new passphrase." subtitle="Minimum 12 characters. Spaces and long phrases are welcome.">
      <form onSubmit={submit} className="space-y-5" noValidate>
        <Field id="new-password" label="New password">
          <PasswordInput
            id="new-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            testId="reset-password-new-input"
          />
        </Field>
        <Field id="confirm-password" label="Confirm password" error={mismatch ? "Passwords do not match." : null} testId="reset-password-confirm">
          <PasswordInput
            id="confirm-password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            invalid={mismatch}
            required
            testId="reset-password-confirm-input"
          />
        </Field>
        <PasswordPolicyHint password={password} policy={policy} />
        {error ? (
          <div role="alert" data-testid="reset-password-error" className="bz-alert-error">
            {error}
          </div>
        ) : null}
        <button type="submit" disabled={busy || !password || !confirm || mismatch} data-testid="reset-password-submit-btn" className="bz-btn-primary w-full">
          {busy ? <Spinner /> : null}
          Set new password
        </button>
      </form>
    </AuthLayout>
  );
}
